"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getMapComponents, type SearchHookRetrieveResult } from "@repo/maps";
import { useAtom } from "jotai";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useId, useRef, useState } from "react";

export function LocationSearchBox({
  initialLocationName,
  isLoading = false,
  syncFilters = true,
  onLocationChange,
}: {
  initialLocationName?: string;
  isLoading?: boolean;
  syncFilters?: boolean;
  onLocationChange?: (
    location: SearchHookRetrieveResult["locations"][number] | null,
  ) => void;
}) {
  const t = useTranslations();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const [searchValue, setSearchValue] = useState<string>(
    initialLocationName ?? (syncFilters ? filters.location?.name : "") ?? "",
  );
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const updateSearchValueRef = useRef<((value: string) => void) | null>(null);
  const hideResultsRef = useRef<(() => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { SearchBox } = getMapComponents(
    process.env.NEXT_PUBLIC_MAPS_PROVIDER as any,
  );

  useEffect(() => {
    if (
      syncFilters &&
      filters.location?.name &&
      filters.location.name !== searchValue
    ) {
      setSearchValue(filters.location.name);
    }
  }, [filters.location?.name, syncFilters]);

  useEffect(() => {
    if (initialLocationName) setSearchValue(initialLocationName);
  }, [initialLocationName]);

  // Dismiss results dropdown when tapping outside on mobile
  useEffect(() => {
    function handlePointerDown(e: PointerEvent | TouchEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        hideResultsRef.current?.();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown, {
      passive: true,
    });
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  async function handleSearchResultRetrieved(result: SearchHookRetrieveResult) {
    if (result.locations.length > 0) {
      const firstLocation = result.locations[0];
      setLocationError(null);
      setIsSelectingLocation(true);
      setSearchValue(firstLocation.name);
      onLocationChange?.(firstLocation);
      if (syncFilters) {
        setFilters((prev) => ({
          ...prev,
          location: firstLocation,
        }));
      }
    }
  }

  function handleClear() {
    setLocationError(null);
    setSearchValue("");
    onLocationChange?.(null);
    if (updateSearchValueRef.current) {
      updateSearchValueRef.current("");
    }
  }

  useEffect(() => {
    if (!isLoading) {
      setIsSelectingLocation(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isSelectingLocation) return;
    const timer = setTimeout(() => setIsSelectingLocation(false), 1600);
    return () => clearTimeout(timer);
  }, [isSelectingLocation]);

  function handleCurrentLocationError(error: Error | GeolocationPositionError) {
    setIsSelectingLocation(false);
    inputRef.current?.focus();
    hideResultsRef.current?.();

    const isPermissionDenied =
      "code" in error && typeof error.code === "number" && error.code === 1;

    setLocationError(
      isPermissionDenied
        ? t("saas.search.filters.fields.location.error.denied")
        : t("saas.search.filters.fields.location.error.unavailable"),
    );
  }

  const showLoadingBar = isLoading || isSelectingLocation;

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current =
      node;
  }, []);

  return (
    <div ref={containerRefCallback} className="relative z-30 min-w-0 flex-1">
      <SearchBox
        token={process.env.NEXT_PUBLIC_MAP_TOKEN as string}
        types={[
          "country",
          "region",
          "postcode",
          "district",
          "place",
          "locality",
          "neighborhood",
          "street",
          "address",
        ]}
        containerProps={{
          className: "w-full",
        }}
        resultsContainerProps={{
          className:
            "absolute left-0 top-12 z-[120] w-full overflow-hidden rounded-md border border-border bg-card shadow-[0_10px_28px_rgba(0,0,0,0.12)]",
        }}
        renderInput={({ updateSearchValue, displayResults, hideResults }) => {
          updateSearchValueRef.current = updateSearchValue;
          hideResultsRef.current = hideResults;
          return (
            <div className="relative rounded-md">
              <Icon
                icon="MagnifyingGlass"
                className="absolute top-1/2 left-4 -translate-y-1/2 text-muted-foreground"
              />

              <Input
                ref={inputRef}
                value={searchValue}
                aria-describedby={locationError ? errorId : undefined}
                aria-label={t(
                  "saas.search.filters.fields.location.placeholder",
                )}
                className="h-11 pl-12 pr-9 text-base md:text-sm"
                placeholder={t(
                  "saas.search.filters.fields.location.placeholder",
                )}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  updateSearchValue(e.target.value);
                  if (locationError) setLocationError(null);
                }}
                onFocus={() => {
                  displayResults();
                }}
                onBlur={hideResults}
              />

              {searchValue && !showLoadingBar && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                  className="absolute top-1/2 right-3 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Clear location"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              )}

              <div
                className={cn(
                  "pointer-events-none absolute right-[1px] bottom-[1px] left-[1px] h-[2px] overflow-hidden rounded-b-[7px] transition-opacity",
                  showLoadingBar ? "opacity-100" : "opacity-0",
                )}
              >
                <div className="search-input-progress-bar h-full bg-success" />
              </div>
            </div>
          );
        }}
        renderSuggestion={({ suggestion, onSelect }) => (
          <Button
            onClick={() => onSelect(suggestion)}
            variant="ghost"
            className="w-full h-auto flex-col items-start text-left rounded-none"
          >
            <strong>{suggestion.name}</strong>
            <div>{suggestion.formattedLocation}</div>
          </Button>
        )}
        renderEmpty={({ currentLocationButton }) => currentLocationButton}
        renderCurrentLocationButton={({
          useCurrentLocation: selectCurrentLocation,
        }) => (
          <Button
            onClick={async (e) => {
              e.preventDefault();

              try {
                const permStatus = await navigator.permissions?.query({
                  name: "geolocation",
                });
                if (permStatus?.state === "denied") {
                  handleCurrentLocationError(
                    Object.assign(new Error("Permission denied"), { code: 1 }),
                  );
                  return;
                }
              } catch {
                // navigator.permissions.query not supported (e.g. iOS Safari <16);
                // fall through to let getCurrentPosition handle it
              }

              setIsSelectingLocation(true);
              selectCurrentLocation();
            }}
            variant="ghost"
            className="w-full h-auto justify-start rounded-none font-semibold py-4"
          >
            <Icon icon="Locate" />
            {t("saas.search.filters.fields.location.results.current")}
          </Button>
        )}
        onRetrieved={handleSearchResultRetrieved}
        onCurrentLocationError={handleCurrentLocationError}
      />
      {locationError ? (
        <output
          id={errorId}
          className="mt-2 flex min-w-0 items-start gap-1.5 text-xs leading-5 text-muted-foreground"
        >
          <Icon
            icon="XCircle"
            aria-hidden="true"
            className="mt-1 size-3 shrink-0"
          />
          <span className="min-w-0 break-words">{locationError}</span>
        </output>
      ) : null}
    </div>
  );
}

LocationSearchBox.displayName = "LocationSearchBox";
